import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Logo from "../../images/logo.svg";
import React, { Fragment } from "react";
import "./PaymentPage.css";
import { t } from "i18next";

// Moyasar's payment-form widget doesn't expose a documented failure/abort
// callback for Apple Pay (only `on_completed` for success). To still show a
// clear error instead of letting the Apple Pay sheet silently vanish, we
// detect the pattern directly at the ApplePaySession level: a legitimate
// user-initiated cancel takes several seconds of human reaction time, while
// a merchant-validation failure aborts the session near-instantly. If
// `abort()` follows `begin()` within this window, treat it as a failure.
const FAST_ABORT_THRESHOLD_MS = 4000;

function instrumentApplePayFailureDetection(onLikelyFailure) {
  if (!window.ApplePaySession || window.ApplePaySession.__matjarPatched) {
    return;
  }
  window.ApplePaySession.__matjarPatched = true;

  const proto = window.ApplePaySession.prototype;
  const originalBegin = proto.begin;
  const originalAbort = proto.abort;
  let beginTimestamp = null;

  proto.begin = function (...args) {
    beginTimestamp = Date.now();
    return originalBegin.apply(this, args);
  };

  proto.abort = function (...args) {
    if (beginTimestamp !== null && Date.now() - beginTimestamp < FAST_ABORT_THRESHOLD_MS) {
      onLikelyFailure();
    }
    beginTimestamp = null;
    return originalAbort.apply(this, args);
  };
}

const PaymentApplepayPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderid");
  const rawAmount = searchParams.get("amount");
  // Moyasar requires an integer amount in halalas; 88.55 * 100 gives
  // 8854.999999999999 in floating point, which the API rejects.
  const amount = Math.round(Number(rawAmount) * 100);
  const navigate = useNavigate();
  const paymentRef = useRef(null);
  const [applePayFailed, setApplePayFailed] = useState(false);

  // callback_url must point at the domain the customer is actually on;
  // a build-time env value once leaked http://localhost:3036 into the
  // production bundle and sent customers there after payment.
  const BASE_URL = window.location.origin;

  useEffect(() => {
    if (window.Moyasar && paymentRef.current) {
      try {
        instrumentApplePayFailureDetection(() => setApplePayFailed(true));

        window.Moyasar.init({
          element: paymentRef.current,
          amount: amount,
          currency: "SAR",
          description: "Order Payment",
          publishable_api_key: process.env.REACT_APP_MOYASAR_PUBLISHABLE_KEY,
          callback_url: `${BASE_URL}/order_received?orderid=${orderId}&amount=${amount}`,
          methods: ["applepay"],
          apple_pay: {
            country: "SA",
            label: "Matjar Alharam | متجر الحرم",
            validate_merchant_url: "https://api.moyasar.com/v1/applepay/initiate",
          },
          on_completed: async (payment) => {
            // Best-effort notify; a failure here must never abort
            // Moyasar's own redirect to callback_url, which is what the
            // mobile app watches to detect payment completion.
            try {
              await fetch(`${BASE_URL}/order_received`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  payment_id: payment.id,
                  order_id: orderId,
                  status: payment.status,
                }),
              });
            } catch (err) {
              console.error("[ApplePayPage] on_completed notify failed:", err);
            }
          },
        });
      } catch (err) {
        console.error("[ApplePayPage] Moyasar.init() threw:", err);
        setApplePayFailed(true);
      }
    }
  }, [orderId, amount, navigate, BASE_URL]);

  const handleTryAgain = () => {
    // Reload for a fresh ApplePaySession/Moyasar widget instance rather than
    // reusing one that may hold stale internal state after a failed attempt.
    window.location.reload();
  };

  const handleUseCreditCard = () => {
    navigate(`/payment-creditcard?orderid=${orderId}&amount=${rawAmount}`);
  };

  return (
    <Fragment>
      <div className="logo-container">
        <Link to="/home">
          <img src={Logo} alt="logo" className="app-logo" />
        </Link>
      </div>
      <div className="payment-container">
        <div className="payment-header">
          <h2>{t("paymentDetails")}</h2>
          <p>
            {t("totalAmount")}: {amount / 100} {t("currency")}
          </p>
        </div>

        {applePayFailed ? (
          <div className="applepay-error">
            <p className="applepay-error-title">{t("applePayUnavailable")}</p>
            <p className="applepay-error-desc">{t("applePayUnavailableDesc")}</p>
            <div className="applepay-error-actions">
              <button
                type="button"
                className="applepay-error-btn applepay-error-btn-primary"
                onClick={handleUseCreditCard}
              >
                {t("creditCard")}
              </button>
              <button
                type="button"
                className="applepay-error-btn"
                onClick={handleTryAgain}
              >
                {t("tryAgain")}
              </button>
            </div>
          </div>
        ) : (
          <div ref={paymentRef} className="mysr-form"></div>
        )}
      </div>
    </Fragment>
  );
};

export default PaymentApplepayPage;
